// src/modules/Rules/components/modals/ReservedModal.tsx
import { forwardRef, useImperativeHandle, useRef, useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ModalCenter, ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { LoadingState } from '../feedback';
import { faBagShopping } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Product as ProductInfo } from '@shared/components/ui/Product';
import { Badge } from '@shared/components/ui/badge';
import { Shop } from '@shared/components/ui/shop';
import { Alert, AlertDescription } from '@shared/components/ui/shadcn/alert';
import { faInfoCircle } from '@fortawesome/pro-light-svg-icons';
import { VirtualTable } from '@components/ui/VirtualTable';
import { ProductsApiAdapter } from '@modules/Products/List/adapters/ProductsApiAdapter';
import { createHttpClient } from '@adapters/HttpClient';
import { apiClientWH } from '@shared/services';
import { ReservedOrder, ReservedOrderApiResponse } from '@modules/Products/List/models/ReservedOrder';
import { isMobile } from 'react-device-detect';
import { VirtualList } from '@components/ui/VirtualList';

export interface ReservedModalProductData {
  name: string;
  code: string;
  image?: string;
  reservedCount: number;
  id: number;
}

export interface ReservedModalRef {
  open: (product: ReservedModalProductData) => void;
}

const productsApi = new ProductsApiAdapter(createHttpClient(apiClientWH));

// Componente simple para mostrar una orden reservada en mobile
const ReservedOrderCard = ({ order, productId }: { order: ReservedOrder; productId: number }) => {
  const qty = order.lines
    .filter((l: any) => l.product_id === productId)
    .reduce((sum: number, l: any) => sum + l.qty, 0);
  return (
    <div className="py-3 border-b border-card-border">
      {/* Número de orden */}
      <span className="font-medium text-base">{order.ref}</span>
      <div className="flex items-center gap-2 w-full">
        {/* Tienda */}
        <Shop name={order.shop?.name} src={order.shop?.image_hash} />

        {/* Unidades */}
        <Badge variant="outline" className="text-xs py-1">
          Unidades: {qty}
        </Badge>

        {/* Estado */}
        <span>
          <Badge
            variant="default"
            className={
              `text-xs bg-card-border ` +
              (order.status?.description === 'Por preparar'
                ? 'text-warning light:!bg-bg-warning'
                : 'text-badge-status-3')
            }
          >
            {order.status?.description || ''}
          </Badge>
        </span>
      </div>
    </div>
  );
};

export const ReservedModal = forwardRef<ReservedModalRef>((_, ref) => {
  const { t } = useTranslation('productsModule');
  const modalRef = useRef<ModalCenterRef>(null);
  const [product, setProduct] = useState<ReservedModalProductData | null>(null);
  const [orders, setOrders] = useState<ReservedOrder[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useImperativeHandle(ref, () => ({
    open: (productData: ReservedModalProductData) => {
      setProduct(productData);
      modalRef.current?.openModal();
    },
  }));

  useEffect(() => {
    if (product?.id) {
      setLoading(true);
      setError(null);
      productsApi
        .getReservedOrdersByProduct({ productId: product.id })
        .then((resp: ReservedOrderApiResponse) => {
          setOrders(resp.data || []);
        })
        .catch(() => {
          setError('Error al cargar las órdenes reservadas');
        })
        .finally(() => setLoading(false));
    } else {
      setOrders([]);
    }
  }, [product?.id]);

  const columns = useMemo(
    () => [
      {
        id: 'ref',
        header: t('reserved_orders_table_ref', { defaultValue: 'Referencia' }),
        accessorKey: 'ref',
        size: 10,
        cell: ({ row }: any) => row.original.ref,
      },
      {
        id: 'shop',
        header: t('reserved_orders_table_shop', { defaultValue: 'Tienda' }),
        accessorKey: 'shop.name',
        size: 20,
        cell: ({ row }: any) => <Shop name={row.original.shop?.name} src={row.original.shop?.image_hash} />,
      },
      {
        id: 'status',
        header: t('reserved_orders_table_status', { defaultValue: 'Estado' }),
        accessorKey: 'status.description',
        size: 20,
        cell: ({ row }: any) => (
          <Badge
            variant="default"
            className={
              `text-xs bg-card-border ` +
              (row.original.status?.description === 'Por preparar'
                ? 'text-warning light:!bg-bg-warning'
                : 'text-badge-status-3')
            }
          >
            {row.original.status?.description || ''}
          </Badge>
        ),
      },
      {
        id: 'qty',
        header: t('reserved_orders_table_qty', { defaultValue: 'Cantidad' }),
        accessorKey: 'qty',
        size: 20,
        cell: ({ row }: any) => {
          const qty = row.original.lines
            .filter((l: any) => l.product_id === product?.id)
            .reduce((sum: number, l: any) => sum + l.qty, 0);
          return qty;
        },
      },
    ],
    [t, product?.id],
  );

  return (
    <ModalCenter
      ref={modalRef}
      title={t('reserved_orders_title')}
      icon={<FontAwesomeIcon icon={faBagShopping} className="w-3 h-3" />}
      type="default"
      width="lg"
      className="h-[100dvh] sm:h-auto w-full sm:w-[579px]"
    >
      {product && (
        <div className="space-y-4 pt-3 pb-2">
          <div className="flex items-center gap-3">
            <ProductInfo
              id={product.id}
              name={product.name}
              src={product.image || ''}
              code={product.code}
              showImageModal={false}
            />
            <span className="ml-auto text-xs bg-card-border px-3 py-1 rounded-full font-semibold flex items-center gap-1">
              {product.reservedCount}
            </span>
          </div>
          <Alert className="mt-2">
            <AlertDescription className="flex items-center gap-3">
              <FontAwesomeIcon icon={faInfoCircle} className="w-4 h-4 text-muted-foreground leading-none" />
              {t('reserved_orders_description')}
            </AlertDescription>
          </Alert>
          <div className="pt-2">
            {loading && <LoadingState message={t('loading.message')} className="h-32" />}
            {error && <div className="text-red-500">{error}</div>}
            {!loading && orders.length === 0 && !error && (
              <>
                <div className="text-center py-8 text-subtext">{t('empty.message')}</div>
              </>
            )}
            {!loading &&
              orders.length > 0 &&
              !error &&
              (isMobile ? (
                <VirtualList
                  data={orders}
                  renderItem={({ item }) => <ReservedOrderCard order={item} productId={product.id} />}
                  itemKey={(item) => item.id}
                  estimateItemHeight={90}
                  height="auto"
                  className="bg-transparent"
                  emptyMessage={t('empty.message')}
                />
              ) : (
                <VirtualTable
                  columns={columns}
                  data={orders}
                  autoHeight={true}
                  maxHeight="250px"
                  disableHorizontalScroll={true}
                />
              ))}
          </div>
        </div>
      )}
    </ModalCenter>
  );
});

ReservedModal.displayName = 'ReservedModal';
